import React, {FC, memo, useEffect, useState} from "react";
import {Button, Spin} from "antd";
import {FormikProps} from "formik";
import moment from "moment";
import {CloseOutlined, EditOutlined, PlusCircleOutlined} from "@ant-design/icons";
import {blue, red} from "@ant-design/colors";
import classNames from "classnames";
import SummarySection from "../../../views/SummarySection/SummarySection";
import {renderAmount} from "../../../helpers/renderAmount";
import css from "./CoursePricePreview.less";
import cssMollie from "../CourseViews/MollieCoursePanel.less";
import i18n from "../../../services/i18n";
import {
    CreateInstallmentRateByCourseCreateInput,
    Currency,
    NewInstallmentPlanInput,
    PaymentInterval,
    PaymentType,
    useGetInstallmentPlanQuery,
    Maybe,
    MonthlySchedule,
    InstallmentType,
} from "../../../generated/graphql";
import {useCurrency} from "../../../helpers/currencyHelper";
import CourseLessonPreviewTable from "./CourseLessonPreviewTable/CourseLessonPreviewTable";
import {PossibleFormikPropsType} from "./CourseCreateForm";
import EditRateModal from "../EditRateModal";
import {updateTotalPrice} from "../helper/updateTotalPrice";
import ResetScheduledPaymentsForCourse from "./ResetScheduledPaymentsForCourse";
import {colorBackgroundBase} from "../../../styles/colors";

type CoursePricePreviewProps = {
    formikProps: FormikProps<PossibleFormikPropsType>;
    style?: React.CSSProperties;
    esrActive: boolean;
};

/**
 * formatMollieDueDate — Berechnet das Mollie-Fälligkeitsdatum für die Preview-Bar
 * (Phase 22-04 Task 3, COURSE-07).
 *
 * Logik analog zu CustomInvoiceDueDaysInput.tsx (Z.21):
 *   - customInvoiceDueDays > 0 ⇒ N Tage VOR Kursbeginn
 *   - customInvoiceDueDays < 0 ⇒ N Tage NACH Kursbeginn
 *   - customInvoiceDueDays === null/undefined ⇒ Fallback auf den Kursbeginn selbst
 *     (im MVP-Scope; späteres "orgDefault" Override liegt im Backend).
 *
 * Node 14 kompatibel — verwendet moment (bereits im Projekt) statt arr.at/replaceAll.
 */
const formatMollieDueDate = (
    customInvoiceDueDays: number | null | undefined,
    courseStartDate: string | Date | null | undefined,
): string => {
    if (!courseStartDate) {
        return "—";
    }
    const start = moment(courseStartDate);
    if (!start.isValid()) {
        return "—";
    }
    if (customInvoiceDueDays == null) {
        // Fallback: Kursbeginn selbst (org-Default-Berechnung wäre Backend-Sache).
        return start.format("DD.MM.YYYY");
    }
    // Positiv = vor Kursbeginn (subtract); negativ = nach Kursbeginn (add absoluten Wert).
    const days = customInvoiceDueDays;
    const result = days >= 0 ? start.clone().subtract(days, "days") : start.clone().add(Math.abs(days), "days");
    return result.format("DD.MM.YYYY");
};

const CoursePricePreview: FC<CoursePricePreviewProps> = ({formikProps, style, esrActive}) => {
    const currency = useCurrency();
    const [currentInstallmentRates, setCurrentInstallmentRates] = useState<Array<CreateInstallmentRateByCourseCreateInput> | undefined>(formikProps.values.installmentRates ?? undefined);

    const {values} = formikProps;

    useEffect(() => {
        if (values.allowMonthlyPayment && esrActive) {
            formikProps.setFieldValue("paymentType", PaymentType.PrePaid);
            formikProps.setFieldValue("allowMonthlyPayment", false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [esrActive]);

    const {lessons, monthlySchedule, paymentInterval, vat, fee} = values;

    const courseFee = fee ?? 0;
    const courseVat = vat ?? 0;

    const grossPrice = paymentInterval === "PerCourse" ? courseFee : courseFee * lessons.length;
    const netPrice = grossPrice / (1 + courseVat);

    // Phase 22-04 — Mollie-Mode Strategy-Branch (Sketch 002 Variant D, COURSE-07).
    // Bei aktivem Mollie-Modus ersetzt die "Vorkasse · via Mollie"-Preview-Bar
    // die monatliche Raten-Liste. Tenants ohne featureMollie sehen NIEMALS
    // allowMolliePayment === true (Mollie-Card wird gar nicht gerendert) ⇒
    // R-5 Byte-Identity bleibt gewahrt.
    const isMollieMode = (values as {allowMolliePayment?: boolean | null}).allowMolliePayment === true;
    const courseStartDate = lessons && lessons.length > 0 ? lessons[0].startDateTime : null;
    const customInvoiceDueDays = (values as {customInvoiceDueDays?: number | null}).customInvoiceDueDays;

    const MonthlyPaymentList = () => {
        const newInstallmentPlanInput: NewInstallmentPlanInput = {
            coursePrice: grossPrice,
            vat: courseVat,
            paymentInterval: values.paymentInterval ?? PaymentInterval.PerCourse,
            // paymentType: PaymentType.Monthly,
            paymentType: esrActive ? PaymentType.PrePaid : values.paymentType ?? PaymentType.Monthly,
            monthlySchedule,
            courseLessonStartDates: lessons.map((lesson) => lesson.startDateTime),
            dateOfFirstInstallment: values.dateOfFirstInstallment,
            discount: values.discount,
            discountUnit: values.discountUnit,
            customInvoiceDueDays: values.customInvoiceDueDays,
        };

        // GET SCHEDULES DEPENDING ON EXISTING OR NEW COURSE --------------------------------------------------------------------------------

        // needs to be skiped if existing course
        const {data: installmentPlanData, refetch: refetchInstallmentPlan} = useGetInstallmentPlanQuery({
            skip: values.monthlySchedule === MonthlySchedule.Custom,
            variables: {newInstallmentPlanInput},
            fetchPolicy: "network-only",
        });

        const installmentPlanPreview = installmentPlanData?.getInstallmentPlanPreview["schedule"];

        // HANDLE monthlySchedule CHANGE --------------------------------------------------------------------------------

        useEffect(() => {
            if (monthlySchedule !== MonthlySchedule.Custom && currentInstallmentRates !== installmentPlanPreview && installmentPlanPreview !== undefined) {
                setCurrentInstallmentRates(installmentPlanPreview);
                formikProps.setFieldValue("installmentRates", installmentPlanPreview);
            }
        }, [installmentPlanPreview]);

        // -----------------------------------------------------------------------------------------------------------------

        const handleRemove = (installmentDue: string) => {
            // find the installmentRate with the installmentDue in currentInstallmentRates
            const installmentRate = currentInstallmentRates?.find((rate) => rate.installmentDue === installmentDue);

            // if installmentRate is found, remove it from currentInstallmentRates
            if (installmentRate) {
                const newInstallmentRates = currentInstallmentRates?.filter((rate) => rate.installmentDue !== installmentDue);

                setCurrentInstallmentRates(newInstallmentRates);
                formikProps.setFieldValue("monthlySchedule", MonthlySchedule.Custom);

                if (newInstallmentRates) {
                    formikProps.setFieldValue("installmentRates", newInstallmentRates);
                    // update TotalPrice
                    updateTotalPrice(newInstallmentRates, formikProps);
                }
            }
        };

        const [modifiedInstallmentRate, setModifiedInstallmentRate] = useState<Maybe<CreateInstallmentRateByCourseCreateInput> | undefined>(undefined);

        const handleEdit = (installmentDue: string) => {
            // find the installmentRate with the installmentDue in currentInstallmentRates
            const installmentRate = currentInstallmentRates?.find((rate) => rate.installmentDue === installmentDue);

            setModifiedInstallmentRate(installmentRate);
            if (values.monthlySchedule !== MonthlySchedule.Custom) {
                formikProps.setFieldValue("monthlySchedule", MonthlySchedule.Custom);
            }
        };

        const addNewRate = () => {
            if (!currentInstallmentRates || currentInstallmentRates?.length === 0) {
                const newInstallmentRate: CreateInstallmentRateByCourseCreateInput = {
                    installmentAmount: 0,
                    installmentDue: moment().format("YYYY-MM-DD"),
                };

                const newInstallmentRates = [...(currentInstallmentRates ?? []), newInstallmentRate];

                setCurrentInstallmentRates(newInstallmentRates);
                formikProps.setFieldValue("installmentRates", newInstallmentRates);
                updateTotalPrice(newInstallmentRates, formikProps);
                setModifiedInstallmentRate(newInstallmentRate);
                return;
            }

            // find the latest due date in currentInstallmentRates and create a variable `newInstallmentDue` with the next month
            const newInstallmentDue = moment(currentInstallmentRates?.slice(-1)[0].installmentDue).add(1, "month").format("YYYY-MM-DD");

            const newInstallmentAmount = currentInstallmentRates?.slice(-1)[0].installmentAmount ?? 0;

            const newInstallmentRate: CreateInstallmentRateByCourseCreateInput = {
                installmentAmount: newInstallmentAmount,
                installmentDue: newInstallmentDue,
            };

            const newInstallmentRates = [...(currentInstallmentRates ?? []), newInstallmentRate];

            setCurrentInstallmentRates(newInstallmentRates);
            formikProps.setFieldValue("installmentRates", newInstallmentRates);
            updateTotalPrice(newInstallmentRates, formikProps);
            setModifiedInstallmentRate(newInstallmentRate);
        };

        if (currentInstallmentRates) {
            return (
                <div style={{paddingTop: 15}}>
                    {currentInstallmentRates.map((rateData, index) => {
                        if (rateData.installmentDue === modifiedInstallmentRate?.installmentDue) {
                            return (
                                <EditRateModal
                                    rate={rateData}
                                    index={index}
                                    formikProps={formikProps}
                                    updateCurrentInstallmentRate={(rates) => {
                                        setCurrentInstallmentRates(rates);
                                    }}
                                />
                            );
                        }

                        if (rateData.installmentType === InstallmentType.Discount) {
                            return (
                                <div
                                    key={rateData.installmentDue}
                                    className={classNames(css.monthlyPaymentEntry, css.memberPriceContainer)}
                                    style={{
                                        backgroundColor: colorBackgroundBase,
                                        color: red[3],
                                        paddingBottom: 7,
                                    }}
                                >
                                    <div className={css.main}>
                                        <div className={css.month}>
                                            <span className={css.num}>{``}</span>
                                            <span>{"Rabatt für Mitglieder"}</span>
                                        </div>
                                        <div className={css.amount}>
                                            {renderAmount({
                                                amount: rateData.installmentAmount ?? 0,
                                                currency: currency.short as Currency,
                                            })}
                                        </div>
                                    </div>
                                    <div className={css.sub}>{`Dieser Bertrag wird bei der Buchung für Mitglieder abgezogen.`}</div>
                                </div>
                            );
                        }

                        return (
                            <div key={rateData.installmentDue} className={css.monthlyPaymentEntry}>
                                <div className={css.main}>
                                    {/* <div className={css.num}>{`${index +
                                            1}`}</div> */}
                                    <div className={css.month}>
                                        <span className={css.num}>{`${index + 1}.`}</span>
                                        <span>{moment(rateData.installmentDue).format("MMM YY")}</span>
                                    </div>
                                    <div className={css.amount}>
                                        {renderAmount({
                                            amount: rateData.installmentAmount ?? 0,
                                            currency: currency.short as Currency,
                                        })}
                                    </div>
                                    {values.monthlySchedule === MonthlySchedule.Custom && (
                                        <>
                                            <Button className={css.editButton} size="small" type="link" icon={<EditOutlined onClick={() => handleEdit(rateData.installmentDue)} />} />
                                            <Button
                                                className={css.editButton}
                                                size="small"
                                                type="link"
                                                style={{color: "red"}}
                                                icon={<CloseOutlined onClick={() => handleRemove(rateData.installmentDue)} />}
                                            />
                                        </>
                                    )}
                                </div>
                                <div className={css.sub}>
                                    {`Zahlbar bis
                                    ${moment(new Date(rateData.installmentDue).toISOString()).format("DD.MM.YY")}`}
                                </div>
                            </div>
                        );
                    })}
                    {values.monthlySchedule === MonthlySchedule.Custom && (
                        <Button
                            block
                            size="small"
                            icon={<PlusCircleOutlined />}
                            style={{
                                color: blue[4],
                                border: `1px solid ${blue[4]}`,
                                borderRadius: 3,
                                backgroundColor: blue[0],
                                marginTop: 5,
                                marginBottom: 5,
                            }}
                            onClick={() => addNewRate()}
                        >
                            {"Neue Rate hinzufügen"}
                        </Button>
                    )}
                </div>
            );
        }

        return <Button onClick={async () => refetchInstallmentPlan}>{"Refetch"}</Button>;

        return <h3>{"Keine Daten"}</h3>;
    };

    const TotalPrice = () => {
        const vatToShow = vat ? vat * 100 : 0;

        const {discount, discountUnit} = values;
        let memberPrice;

        if (discount) {
            if (discountUnit === "Percentage") {
                memberPrice = grossPrice - grossPrice * (discount / 100);
            } else {
                memberPrice = grossPrice - discount;
            }
        }

        return (
            <Spin spinning={false}>
                <div className={css.totalPrice}>
                    <div>{"Gesamtpreis"}</div>
                    <div>
                        {renderAmount({
                            amount: grossPrice ?? 0,
                            currency: currency.short as Currency,
                        })}
                    </div>
                </div>
                <div className={css.totalPriceSub}>
                    {`netto ${renderAmount({
                        amount: netPrice,
                        currency: currency.short as Currency,
                    })} | 
                    ${vatToShow.toFixed(0)} % Mwst.`}
                </div>
                {memberPrice && (
                    <div className={css.memberPriceContainer} style={{marginBottom: 5}}>
                        {/* <Divider style={{margin: 3}} /> */}
                        <div className={css.memberPrice}>
                            <div>{"Preis für Mitglieder"}</div>
                            <div>
                                {renderAmount({
                                    amount: memberPrice,
                                    currency: currency.short as Currency,
                                })}
                            </div>
                        </div>
                        <div className={css.totalPriceSub}>{`Rabatt: ${values.discount} ${values.discountUnit === "Percentage" ? "%" : currency.symbol}`}</div>
                    </div>
                )}
            </Spin>
        );
    };

    return (
        <div style={style}>
            <SummarySection
                title={i18n.containers.bookings.BookingSummary.sectionHeaders.courseLessons()}
                content={
                    <>
                        <CourseLessonPreviewTable courseLessons={values.lessons} totalPrice={grossPrice ?? 0} paymentInterval={paymentInterval ?? undefined} />
                    </>
                }
                style={{paddingTop: 0}}
            />
            {isMollieMode ? (
                <SummarySection
                    style={{paddingTop: 10}}
                    content={
                        <div className={cssMollie.molliePreviewBar} data-testid="MolliePreviewBar">
                            <div className={cssMollie.molliePreviewBarRow}>
                                <span className={cssMollie.mollieLogo}>{"mollie"}</span>
                                <strong>{"Vorkasse · via Mollie"}</strong>
                            </div>
                            <div className={cssMollie.molliePreviewBarRow}>
                                <strong>
                                    {renderAmount({
                                        amount: grossPrice ?? 0,
                                        currency: currency.short as Currency,
                                    })}
                                </strong>
                            </div>
                            <div className={cssMollie.dueDate}>
                                {"Zahlbar bis "}
                                {formatMollieDueDate(customInvoiceDueDays, courseStartDate)}
                            </div>
                        </div>
                    }
                />
            ) : (
                <SummarySection
                    style={{paddingTop: 10}}
                    title={values.paymentType === PaymentType.Monthly ? i18n.containers.bookings.BookingSummary.installmentRates() : undefined}
                    content={<MonthlyPaymentList />}
                />
            )}
            <SummarySection content={<TotalPrice />} />
            <ResetScheduledPaymentsForCourse formikProps={formikProps} courseId={(values as {id?: string}).id} />
        </div>
    );
};

export default memo(CoursePricePreview);
